/* eslint-disable camelcase */
// @flow

import OAuth from './OAuth';
import createDebugger from 'debug';
import invariant from 'invariant';

const debug = createDebugger('oauth:FacebookOAuth');
declare var FB: any;

export type IFacebookLoginStatus = {
  status: 'connected' | 'not_authorized' | 'unknown',
  authResponse?: {
    accessToken: string,
    expiresIn: string,
    reauthorize_required_in: string,
    signedRequest: string,
    userID: string
  }
};

export type IFacebookProfile = {
  id: string,
  firstName: string,
  lastName: string,
  email?: string
};

const localStorageKeys = {
  FB_REDIRECTION: 'xtime:facebookSetRedirection',
  FB_PERSONID: 'xtime:facebookPersonId'
};

/**
 * Facebook OAuth provider
 */
class FacebookOAuth extends OAuth {
  name = 'facebook';
  scope = 'public_profile,email';
  defaultClientId = '896920353846839';
  /**
   * True if the page was reloaded because of the FB SDK limitation
   */
  doRedirection = false;
  /**
   * Set in the case where the social login redirection is set and the
   * appointment was created from the profile page, this is only set during
   * provider initialization (see middleware/oauth)
   */
  personId: number | void;

  isSDKLoaded() {
    // see public/index.html
    return window.fbAsyncInit.initialized;
  }

  /**
   * Invoked only once to initialize this provider, {@see #initialize}
   */
  async doInitialize(clientId: string) {
    await this.waitForSDK();
    const params = {
      appId: clientId,
      cookie: true,
      xfbml: true,
      version: 'v3.1'
    };
    debug('facebook sdk initialized with: ', params);
    FB.init(params);
    this.providerInitialized = true;
    this.emit('initialize');
  }

  async login(personId?: string): Promise<IFacebookLoginStatus> {
    await this.waitForSDK();

    const prevToken = FB.getAccessToken();
    return new Promise(resolve =>
      FB.login(resolve, {
        scope: 'email',
        return_scopes: true
        // uncomment the following lines to ask for permissions again
        // auth_type: 'rerequest'
      })
    ).then(loginStatus => {
      return new Promise(resolve => {
        // the sdk doesn't load the iframe on first login
        if (!prevToken && loginStatus.status === 'connected') {
          debug('setting flags in localStorage');
          window.localStorage.setItem(localStorageKeys.FB_REDIRECTION, 'true');
          // when the sign up/login is done in the confirmation page
          // additionally we get a personId that needs to be persisted
          // and restored after the page is reloaded
          if (personId) {
            window.localStorage.setItem(localStorageKeys.FB_PERSONID, personId);
          }
          window.location.reload();
        }
        resolve(loginStatus);
      });
    });
  }

  async logout(): Promise<IFacebookLoginStatus> {
    await this.waitForSDK();
    return new Promise(resolve => FB.logout(resolve));
  }

  async isLoggedIn(): Promise<boolean> {
    await this.waitForSDK();
    return this.getLoginStatus().then(
      response => response.status === 'connected'
    );
  }

  async getLoginStatus(): Promise<IFacebookLoginStatus> {
    await this.waitForSDK();
    return new Promise(resolve => FB.getLoginStatus(resolve));
  }

  async getToken(): Promise<string> {
    await this.waitForSDK();
    const loginStatus = await this.getLoginStatus();
    invariant(loginStatus.authResponse, 'auth response must be set');
    return loginStatus.authResponse.accessToken;
  }

  async getProfile(): Promise<IFacebookProfile> {
    await this.waitForSDK();

    const params = {
      fields: 'email,first_name,last_name'
    };
    return new Promise(resolve =>
      FB.api('/me', params, profile => {
        // transform properties to an entity that the app knows
        profile.firstName = profile.first_name;
        profile.lastName = profile.last_name;
        resolve(profile);
      })
    );
  }

  hasRedirectionFlag(): boolean {
    return (
      window.localStorage.getItem(localStorageKeys.FB_REDIRECTION) === 'true'
    );
  }

  setDoRedirection(): void {
    this.doRedirection = true;
    // set to undefined in case parseInt returns NaN
    // prettier-ignore
    this.personId = parseInt(window.localStorage.getItem(localStorageKeys.FB_PERSONID), 10) || undefined;
    this.emit('needsRedirection');
    debug(
      'setting `doRedirection`, redirection will be done by the SocialLogin component'
    );
  }

  clearRedirectionFlag(): void {
    window.localStorage.removeItem(localStorageKeys.FB_REDIRECTION);
    window.localStorage.removeItem(localStorageKeys.FB_PERSONID);
  }

  needsRedirection(): boolean {
    return this.doRedirection;
  }
}

const provider = new FacebookOAuth();

export default provider;



// WEBPACK FOOTER //
// ./src/utils/oauth/FacebookOAuth.js